/**
 * Utility functions for dashboard data formatting and manipulation
 * Centralizes common formatting operations used across dashboard components
 */

// Currency formatting - Industry standard: no decimals for whole amounts, 2 decimals for cents
export const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
        return 'N/A';
    }
    const numericAmount = Number(amount);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(numericAmount);
};

// Percentage formatting - Industry standard: 1 decimal place for precision
// Expects canonical percentage inputs (0-100)
export const formatPercentage = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return 'N/A';
    }
    return `${numericValue.toFixed(1)}%`;
};

// Currency formatting with fallback for null/undefined values
export const formatCurrencyWithFallback = (amount, fallback = 'N/A') => {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return fallback;
    }
    return formatCurrency(amount);
};

// Percentage formatting with fallback for null/undefined values
export const formatPercentageWithFallback = (value, fallback = 'N/A') => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return fallback;
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return fallback;
    }
    return formatPercentage(numericValue);
};

// Status color mapping
export const getStatusColor = (status) => {
    switch (status) {
        case 'completed': return 'success';
        case 'active': return 'primary';
        case 'on_hold': return 'warning';
        case 'cancelled': return 'error';
        default: return 'default';
    }
};

// Profit color determination
export const getProfitColor = (profit) => {
    if (profit > 0) return 'success';
    if (profit < 0) return 'error';
    return 'default';
};

// Contract type formatting
export const getContractTypeLabel = (type) => {
    return type === 'lump_sum' ? 'Lump Sum' : 'Hourly';
};

export const getContractTypeColor = (type) => {
    return type === 'lump_sum' ? 'primary' : 'secondary';
};

// Chart colors
export const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

// Date formatting for charts
export const formatChartDate = (dateString) => {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (error) {
        return dateString;
    }
};

// Number abbreviation (1000 -> 1K) - Industry standard: 1 decimal place for abbreviated numbers
export const abbreviateNumber = (num) => {
    if (num === null || num === undefined || Number.isNaN(Number(num))) {
        return 'N/A';
    }
    const numericValue = Number(num);
    if (numericValue >= 1000000) {
        return (numericValue / 1000000).toFixed(1) + 'M';
    }
    if (numericValue >= 1000) {
        return (numericValue / 1000).toFixed(1) + 'K';
    }
    return numericValue.toString();
};

// Variance formatting with color - Industry standard: 1 decimal place for variance
export const formatVariance = (variance, isPercentage = false) => {
    if (variance === null || variance === undefined || Number.isNaN(Number(variance))) {
        return {
            value: 'N/A',
            color: 'default'
        };
    }
    const numericVariance = Number(variance);
    const value = isPercentage ? formatPercentage(numericVariance) : numericVariance.toFixed(1);
    const prefix = numericVariance > 0 ? '+' : '';
    return {
        value: prefix + value,
        color: numericVariance > 0 ? 'error' : 'success'
    };
};

// Hours formatting - Industry standard: 1 decimal place for hours
export const formatHours = (hours) => {
    if (hours === null || hours === undefined || Number.isNaN(Number(hours))) {
        return 'N/A';
    }
    const numericHours = Number(hours);
    return `${numericHours.toFixed(1)}h`;
};

// Rate formatting - Industry standard: 2 decimal places for rates
export const formatRate = (rate) => {
    if (rate === null || rate === undefined || Number.isNaN(Number(rate))) {
        return 'N/A';
    }
    const numericRate = Number(rate);
    return `$${numericRate.toFixed(2)}`;
};

// Decimal number formatting - Industry standard: 1-2 decimal places based on context
export const formatDecimal = (value, decimalPlaces = 1) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
    }
    const numericValue = Number(value);
    return numericValue.toFixed(decimalPlaces);
};

// Project health score calculation
export const calculateProjectHealth = (project) => {
    let score = 100;

    // Budget variance impact
    if (project.budget && project.total_cost) {
        const budgetVariance = ((project.total_cost - project.budget) / project.budget) * 100;
        if (budgetVariance > 10) score -= 30;
        else if (budgetVariance > 5) score -= 15;
    }

    // Profit margin impact
    if (project.profit_margin < 10) score -= 20;
    else if (project.profit_margin < 20) score -= 10;

    // Status impact
    if (project.status === 'on_hold') score -= 25;
    else if (project.status === 'cancelled') score = 0;

    return Math.max(0, Math.min(100, score));
};

const toNullableNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const compareWithNullLast = (left, right, sortOrder) => {
    const leftIsNull = left === null;
    const rightIsNull = right === null;

    if (leftIsNull && rightIsNull) return 0;
    if (leftIsNull) return sortOrder === 'asc' ? 1 : -1;
    if (rightIsNull) return sortOrder === 'asc' ? -1 : 1;

    if (sortOrder === 'desc') {
        return right > left ? 1 : right < left ? -1 : 0;
    }

    return left > right ? 1 : left < right ? -1 : 0;
};

// Health score to color mapping
export const getHealthColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
};

// Health score to text mapping
export const getHealthText = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    if (score >= 20) return 'Poor';
    return 'Critical';
};

// Sort projects by various criteria
export const sortProjects = (projects, sortBy = 'name', sortOrder = 'asc') => {
    return [...projects].sort((a, b) => {
        let aVal, bVal;

        switch (sortBy) {
            case 'name':
                aVal = a.name?.toLowerCase() || '';
                bVal = b.name?.toLowerCase() || '';
                break;
            case 'profit':
                aVal = toNullableNumber(a.profit_loss);
                bVal = toNullableNumber(b.profit_loss);
                break;
            case 'margin':
                aVal = toNullableNumber(a.profit_margin);
                bVal = toNullableNumber(b.profit_margin);
                break;
            case 'budget':
                aVal = toNullableNumber(a.budget);
                bVal = toNullableNumber(b.budget);
                break;
            case 'cost':
                aVal = toNullableNumber(a.total_cost);
                bVal = toNullableNumber(b.total_cost);
                break;
            case 'health':
                aVal = calculateProjectHealth(a);
                bVal = calculateProjectHealth(b);
                break;
            default:
                aVal = toNullableNumber(a[sortBy]);
                bVal = toNullableNumber(b[sortBy]);
        }

        return compareWithNullLast(aVal, bVal, sortOrder);
    });
};

// Filter projects by criteria
export const filterProjects = (projects, filters) => {
    return projects.filter(project => {
        // Status filter
        if (filters.status && filters.status !== 'all' && project.status !== filters.status) {
            return false;
        }

        // Profit filter
        if (filters.profitability) {
            const projectProfit = toNullableNumber(project.profit_loss);
            if (projectProfit === null) return false;
            if (filters.profitability === 'profitable' && projectProfit <= 0) return false;
            if (filters.profitability === 'unprofitable' && projectProfit >= 0) return false;
        }

        // Budget filter
        if (filters.budgetStatus) {
            const projectCost = toNullableNumber(project.total_cost);
            const projectBudget = toNullableNumber(project.budget);
            if (projectCost === null || projectBudget === null) return false;

            const isOverBudget = projectCost > projectBudget;
            if (filters.budgetStatus === 'over' && !isOverBudget) return false;
            if (filters.budgetStatus === 'under' && isOverBudget) return false;
        }

        // Contract type filter
        if (filters.contractType && filters.contractType !== 'all' && project.contract_type !== filters.contractType) {
            return false;
        }

        return true;
    });
};

// Generate summary statistics
export const generateSummaryStats = (projects) => {
    if (!projects.length) {
        return {
            totalProjects: 0,
            totalRevenue: 0,
            totalCosts: 0,
            totalProfit: 0,
            averageMargin: 0,
            profitableProjects: 0,
            overBudgetProjects: 0
        };
    }

    const toNullableNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const allValuesFinite = (values) => values.every(value => value !== null);

    const revenueValues = projects.map((p) => toNullableNumber(p.billable_amount));
    const costValues = projects.map((p) => toNullableNumber(p.total_cost));
    const profitLossValues = projects.map((p) => toNullableNumber(p.profit_loss));

    const totalRevenue = allValuesFinite(revenueValues)
        ? revenueValues.reduce((sum, v) => sum + v, 0)
        : null;
    const totalCosts = allValuesFinite(costValues)
        ? costValues.reduce((sum, v) => sum + v, 0)
        : null;
    const totalProfit = totalRevenue === null || totalCosts === null ? null : totalRevenue - totalCosts;
    const averageMargin = totalRevenue > 0 && totalProfit !== null
        ? (totalProfit / totalRevenue) * 100
        : null;
    const validProfitProjects = profitLossValues.filter((v) => v !== null);
    const profitableProjects = validProfitProjects.length ? validProfitProjects.filter((v) => v > 0).length : null;

    const validBudgetPairs = projects
        .map((p) => ({
            budget: toNullableNumber(p.budget),
            cost: toNullableNumber(p.total_cost),
        }))
        .filter(({ budget, cost }) => budget !== null && cost !== null);
    const overBudgetProjects = validBudgetPairs.length
        ? validBudgetPairs.filter(({ budget, cost }) => cost > budget && budget > 0).length
        : null;

    return {
        totalProjects: projects.length,
        totalRevenue,
        totalCosts,
        totalProfit,
        averageMargin,
        profitableProjects,
        overBudgetProjects
    };
}; 